import { memo, useCallback, useContext, useMemo } from "react";
import { InsertRowAboveOutlined } from "@ant-design/icons";

import { tabExists } from "lib/tabs";

import { MenuContext } from "contexts/menu";
import { LoadingContext } from "contexts/loading";
import { DatastoreContext } from "contexts/datastores/datastores";

import IconWrapper from "components/Atoms/Icon/IconWrapper";
import { StyledWrapper, StyledName } from "./styles";
import { useRouter } from "next/router";
import { ZTabTypes } from "zod/Tab";
import ReactGA from "react-ga4";
import { useShallowPush } from "utils/router/shallow-push";

interface DatasetItemProps {
  displayName: string;
  id: string;
}

const DatasetItem = memo(({ displayName, id }: DatasetItemProps) => {
  const { setIsDatastoresMenuActive, setIsDatastoresMenuPinned } =
    useContext(MenuContext);

  const { openTabs, setOpenTabs, activeTab } = useContext(DatastoreContext);
  const { setIsTabLoading, setLoadingTab } = useContext(LoadingContext);
  const isOpen = useMemo(() => activeTab === id, [activeTab, id]);
  const openClass = useMemo(() => (isOpen ? "open" : ""), [isOpen]);
  const { push } = useShallowPush();
  const { orgName } = useRouter().query;
  const onClick = useCallback(async () => {
    if (
      !setIsDatastoresMenuActive ||
      !setIsDatastoresMenuPinned ||
      !openTabs ||
      !setOpenTabs
    )
      return;

    setIsDatastoresMenuPinned(false);
    setIsDatastoresMenuActive(false);

    if (tabExists(openTabs, id)) {
      if (process.env.NODE_ENV === "production") {
        ReactGA.send({
          hitType: "pageview",
          page: `/dataset/${id}/summary`,
          title: "User Hit This Route",
        });
      }
      await push(`/dataset/${id}/summary`);
      return;
    }

    const newTab = {
      id,
      name: displayName,
      type: "dataset" as ZTabTypes,
      orgName: orgName as string,
    };

    const newTabs = [newTab, ...openTabs];
    if (newTabs != openTabs) setOpenTabs(newTabs);
    setIsTabLoading && setIsTabLoading(true);
    setLoadingTab && setLoadingTab(id);

    push(`/dataset/${id}/summary`).then(
      () => setIsTabLoading && setIsTabLoading(false)
    );
  }, [
    setIsDatastoresMenuActive,
    setIsDatastoresMenuPinned,
    openTabs,
    setOpenTabs,
    id,
    displayName,
    setIsTabLoading,
    setLoadingTab,
    push,
  ]);

  if (!openTabs || !setOpenTabs) return null;

  return (
    <StyledWrapper
      onClick={onClick}
      className={`flex items-center ${openClass}`}>
      <IconWrapper icon={<InsertRowAboveOutlined />} />

      <StyledName className="dataset-item-title">{displayName}</StyledName>
    </StyledWrapper>
  );
});

export default DatasetItem;
